import { FC } from 'react';
import classNames from 'classnames';

import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';

import { FOV_IMAGES } from './VideoRoiCalculator';

interface VideoRoiCalculatorFovSelectorProps {
  selectedFov: number;
  onFovChange: (fov: number) => void;
  className?: string;
}

export const VideoRoiCalculatorFovSelector: FC<VideoRoiCalculatorFovSelectorProps> = ({
  selectedFov,
  onFovChange,
  className,
}) => {
  const { t } = useFallbackTranslation();

  return (
    <div
      className={classNames(
        'md:absolute md:bottom-[5%] md:left-1/2 md:-translate-x-1/2',
        className
      )}
    >
      <div
        className="grid grid-cols-3 rounded-full shadow-lg"
        role="radiogroup"
        aria-label={t('system.P50RoiFieldOfViewSelection')}
      >
        {FOV_IMAGES.map((image, index) => (
          <div key={image.fov}>
            <input
              type="radio"
              id={`fov-${image.fov}`}
              name="fov-selector"
              value={image.fov}
              checked={selectedFov === image.fov}
              onChange={() => onFovChange(image.fov)}
              className="peer sr-only"
            />
            <label
              htmlFor={`fov-${image.fov}`}
              className={classNames(
                'flex h-full cursor-pointer flex-col items-center justify-center gap-1 bg-gray-50 py-8 text-center font-medium text-gray-900 transition duration-300 ease-in-out hover:bg-white peer-focus-visible:ring-2 peer-focus-visible:ring-inset peer-focus-visible:ring-focus md:min-w-40 md:p-8 xl:flex-row',
                {
                  'md:rounded-l-full': index === 0,
                  'md:rounded-r-full': index === 2,
                  'bg-yellow': selectedFov === image.fov,
                  'hover:bg-yellow': selectedFov === image.fov,
                  'hover:bg-beige-50': selectedFov !== image.fov,
                }
              )}
            >
              <span className="font-bold">
                {image.fov === 180 ? t('system.P50RoiPanaCast') : t('system.P50RoiVideoBar')}
              </span>{' '}
              <span
                className={classNames(
                  'inline-block rounded-full px-2 transition duration-300 ease-in-out',
                  selectedFov === image.fov ? 'bg-green-700 text-white' : 'bg-gray-100'
                )}
              >
                {image.fov}&deg;
              </span>
            </label>
            <div className="peer-focus-visible:absolute peer-focus-visible:inset-0 peer-focus-visible:ring-2 peer-focus-visible:ring-focus peer-focus-visible:ring-offset-2 md:peer-focus-visible:rounded-full"></div>
          </div>
        ))}
      </div>
    </div>
  );
};
